import {
  Permission,
  TeamsApi,
  type TeamWithCountsDto,
} from '@up2stay/up2promo-api-client';
import { Button, cn } from '@up2stay/up2promo-ui';
import { Users } from 'lucide-react';
import { useIonViewWillEnter } from '@ionic/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

import { UpgradeCard } from '@/core/components/billing/upgrade-surfaces';
import {
  ListEmptyState,
  ListPageBody,
  ListSearchField,
  ListToolbarSurface,
  ResourceCardGrid,
  ResourceCardGridItem,
  ResourceCardGridSkeleton,
} from '@/core/components/layout';
import { CreateTeamInlineCard } from '@/core/components/workspace/teams/create-team-inline-card';
import { DeleteTeamDialog } from '@/core/components/workspace/teams/delete-team-dialog';
import { TeamCard } from '@/core/components/workspace/teams/team-card';
import { TeamFormDialog } from '@/core/components/workspace/teams/team-form-dialog';
import { useEntitlements } from '@/core/hooks/use-entitlements';
import { useNavigateShell } from '@/core/hooks/use-navigate-shell';
import { usePermissions } from '@/core/hooks/use-permissions';
import { useDictionary } from '@/core/providers/dictionary-provider';
import { shellPaths } from '@/core/router/app-paths';
import { useCurrentWorkspace } from '@/core/store/workspace.store';

export function PeopleTeamsTab() {
  const dict = useDictionary();
  const teamsDict = dict.workspace.teams;
  const { navigateShell } = useNavigateShell();
  const currentWorkspace = useCurrentWorkspace();
  const { can } = usePermissions();
  const canManage = can(Permission.TEAMS_MANAGE);
  // DEV-505: creating teams needs the Teams plan; existing teams stay listed.
  const { isFeatureEnabled, isLoading: entitlementsLoading } =
    useEntitlements();
  const canCreateTeams = isFeatureEnabled('feature.invite_members');

  const [teams, setTeams] = useState<TeamWithCountsDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const [filter, setFilter] = useState<'all' | 'mine'>(
    canManage ? 'all' : 'mine',
  );

  const [editTeam, setEditTeam] = useState<TeamWithCountsDto | null>(null);
  const [deleteTeam, setDeleteTeam] = useState<TeamWithCountsDto | null>(null);

  useEffect(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearch(search);
    }, 300);
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [search]);

  const loadTeams = useCallback(async () => {
    if (!currentWorkspace) return;
    setIsLoading(true);
    try {
      const res = await TeamsApi.findAll({
        path: { workspaceId: currentWorkspace.id },
        query: {
          page: 1,
          limit: 100,
          ...(debouncedSearch.trim() ? { search: debouncedSearch.trim() } : {}),
          ...(filter === 'mine' ? { onlyMine: true } : {}),
        },
      });
      setTeams((res.data?.data ?? []) as TeamWithCountsDto[]);
    } catch (err) {
      console.error('Failed to load teams:', err);
      toast.error(teamsDict.errors.loadFailed);
    } finally {
      setIsLoading(false);
    }
  }, [currentWorkspace, debouncedSearch, filter, teamsDict.errors.loadFailed]);

  useEffect(() => {
    if (!currentWorkspace) return;
    void (async () => {
      await loadTeams();
    })();
  }, [currentWorkspace, loadTeams]);

  useIonViewWillEnter(() => {
    void loadTeams();
  });

  const handleOpenTeam = (team: TeamWithCountsDto) => {
    navigateShell(shellPaths.morePeopleTeamDetail(team.id));
  };

  const cardDict = useMemo(
    () => ({
      memberCount: teamsDict.memberCount,
      memberCountPlural: teamsDict.memberCountPlural,
      yourTeamBadge: teamsDict.yourTeamBadge,
      edit: teamsDict.actions.edit,
      delete: teamsDict.actions.delete,
    }),
    [teamsDict],
  );

  if (!currentWorkspace) {
    return (
      <p className="text-[13.5px] leading-relaxed text-muted-foreground">
        {teamsDict.selectWorkspace}
      </p>
    );
  }

  const isSearching = Boolean(debouncedSearch.trim());
  const showAddCard = canManage && canCreateTeams && !isSearching;
  const showCreateUpgrade =
    canManage && !entitlementsLoading && !canCreateTeams;
  const showGrid = isLoading || showAddCard || teams.length > 0;
  const slotCount = teams.length + (showAddCard ? 1 : 0);

  return (
    <ListPageBody>
      {showCreateUpgrade ? (
        <UpgradeCard
          feature="feature.invite_members"
          compact
          title={teamsDict.upgradeTitle}
          description={teamsDict.upgradeDescription}
        />
      ) : null}
      <ListToolbarSurface variant="card">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <ListSearchField
            className="min-w-0 flex-1"
            variant="toolbar"
            placeholder={teamsDict.searchPlaceholder}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onClear={() => setSearch('')}
          />

          {canManage ? (
            <div
              className="inline-flex w-full rounded-[var(--r-control)] border border-border/60 bg-muted/30 p-0.5 sm:w-auto"
              role="group"
              aria-label={teamsDict.filterAll}
            >
              {(
                [
                  {
                    value: 'all' as const,
                    label: teamsDict.filterAll,
                  },
                  {
                    value: 'mine' as const,
                    label: teamsDict.filterMine,
                  },
                ] as const
              ).map((option) => {
                const isSelected = filter === option.value;
                return (
                  <Button
                    key={option.value}
                    type="button"
                    variant="ghost"
                    size="sm"
                    aria-pressed={isSelected}
                    onClick={() => setFilter(option.value)}
                    className={cn(
                      'h-9 flex-1 rounded-[calc(var(--r-control)-2px)] px-3 text-[13.5px] font-medium sm:flex-initial sm:px-4',
                      isSelected
                        ? 'bg-[color:var(--ghost-accent-bg)] text-[color:var(--ghost-accent-fg)] hover:bg-[color:var(--ghost-accent-bg)] hover:text-[color:var(--ghost-accent-fg)]'
                        : 'text-muted-foreground hover:text-foreground',
                    )}
                  >
                    {option.label}
                  </Button>
                );
              })}
            </div>
          ) : null}
        </div>
      </ListToolbarSurface>

      {isLoading ? (
        <ResourceCardGridSkeleton count={3} />
      ) : showGrid ? (
        <ResourceCardGrid slotCount={Math.max(slotCount, 1)}>
          {showAddCard ? (
            <ResourceCardGridItem>
              <CreateTeamInlineCard
                workspaceId={currentWorkspace.id}
                onCreated={() => void loadTeams()}
                dict={{
                  addCard: teamsDict.inlineCreate.addCard,
                  namePlaceholder: teamsDict.form.namePlaceholder,
                  descriptionPlaceholder: teamsDict.form.descriptionPlaceholder,
                  inlineSave: teamsDict.inlineCreate.inlineSave,
                  inlineCancel: teamsDict.inlineCreate.inlineCancel,
                  successMessage: teamsDict.createDialog.success,
                  errorMessage: teamsDict.createDialog.error,
                  errorNameTaken: teamsDict.createDialog.errorNameTaken,
                }}
              />
            </ResourceCardGridItem>
          ) : null}
          {teams.map((team) => (
            <ResourceCardGridItem key={team.id}>
              <TeamCard
                team={team}
                canManage={canManage}
                onOpen={handleOpenTeam}
                onEdit={(t) => setEditTeam(t)}
                onDelete={(t) => setDeleteTeam(t)}
                dict={cardDict}
              />
            </ResourceCardGridItem>
          ))}
        </ResourceCardGrid>
      ) : (
        <ListEmptyState
          icon={<Users className="h-6 w-6 text-muted-foreground" />}
          title={
            isSearching
              ? teamsDict.noResults
              : filter === 'mine'
                ? teamsDict.noTeamsForMember
                : // "Alle Teams" is about the workspace, not the viewer (DEV-538).
                  teamsDict.noTeamsInWorkspace
          }
        />
      )}

      {editTeam && (
        <TeamFormDialog
          open={!!editTeam}
          onOpenChange={(o) => !o && setEditTeam(null)}
          workspaceId={currentWorkspace.id}
          team={editTeam}
          onSuccess={() => void loadTeams()}
          dict={{
            title: teamsDict.editDialog.title,
            description: teamsDict.editDialog.description,
            nameLabel: teamsDict.form.name,
            namePlaceholder: teamsDict.form.namePlaceholder,
            descriptionLabel: teamsDict.form.description,
            descriptionPlaceholder: teamsDict.form.descriptionPlaceholder,
            colorLabel: teamsDict.form.color,
            cancel: teamsDict.form.cancel,
            submitLabel: teamsDict.form.save,
            submittingLabel: teamsDict.form.saving,
            successMessage: teamsDict.editDialog.success,
            errorMessage: teamsDict.editDialog.error,
            errorNameTaken: teamsDict.createDialog.errorNameTaken,
          }}
        />
      )}

      {deleteTeam && (
        <DeleteTeamDialog
          open={!!deleteTeam}
          onOpenChange={(o) => !o && setDeleteTeam(null)}
          workspaceId={currentWorkspace.id}
          teamId={deleteTeam.id}
          teamName={deleteTeam.name}
          onSuccess={() => void loadTeams()}
          dict={{
            title: teamsDict.deleteDialog.title,
            description: teamsDict.deleteDialog.description,
            confirm: teamsDict.deleteDialog.confirm,
            deleting: teamsDict.deleteDialog.deleting,
            cancel: teamsDict.form.cancel,
            success: teamsDict.deleteDialog.success,
            error: teamsDict.deleteDialog.error,
          }}
        />
      )}
    </ListPageBody>
  );
}
